
import React from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Eye, EyeOff, ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCachedTechniqueImage } from '@/hooks/useCachedTechniqueImage';


interface TechniqueCardImageProps {
  imageUrl?: string;
  name: string;
  likeCount?: number;
  isLiked?: boolean;
  isHidden?: boolean;
  likeAnimation?: boolean;
  onLikeToggle?: (e: React.MouseEvent) => void;
  onHideToggle?: (e: React.MouseEvent) => void;
}

export const TechniqueCardImage: React.FC<TechniqueCardImageProps> = ({
  imageUrl,
  name,
  likeCount = 0,
  isLiked = false,
  isHidden = false,
  likeAnimation = false,
  onHideToggle,
}) => {
  const { imageUrl: cachedImageUrl, isLoading } = useCachedTechniqueImage(imageUrl);
  const placeholder = 'https://placehold.co/600x400/e2e8f0/64748b?text=No+Image';

  // Use cached image URL if available, otherwise fall back to original or placeholder
  const displayImageUrl = cachedImageUrl || placeholder;
  
  return (
    <div className="relative w-full overflow-hidden rounded-md">
      <AspectRatio ratio={16 / 9}>
        <img
          src={displayImageUrl}
          alt={name}
          className={cn(
            "object-cover w-full h-full transition-opacity",
            isHidden && "opacity-50",
            isLoading && "opacity-75"
          )}
        />
      </AspectRatio>
      
      {likeCount > 0 && (
        <Badge 
          className={cn(
            "absolute top-2 left-2 bg-black/70 hover:bg-black/70 text-white text-[10px] sm:text-xs",
            isLiked && "bg-primary/90 hover:bg-primary/90"
          )}
        >
          <ThumbsUp className={cn(
            "h-3 w-3 mr-1",
            likeAnimation && "animate-[bounce_0.5s_ease-in-out]"
          )} />
          {likeCount}
        </Badge>
      )}
      
      {/* Removed the thumbs up button from the image as requested */}
      
      {onHideToggle && (
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            "absolute bottom-2 right-2 h-7 w-7 rounded-full bg-black/50 hover:bg-black/60 p-0",
            isHidden && "bg-gray-700/70 hover:bg-gray-700/80 text-white"
          )}
          onClick={onHideToggle}
        >
          {isHidden ? (
            <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" />
          ) : (
            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
          )}
          <span className="sr-only">{isHidden ? 'Show' : 'Hide'}</span>
        </Button>
      )}
    </div>
  );
};
